import React from 'react';
import { getSpeakerColor } from '../../config/participants';
import TrainingDetectionCard, { InquiryCardActions, TrainingQuestionDetection } from './TrainingDetectionCard';

/**
 * One event of a training session's conversation timeline, laid out like the
 * post-call timeline (SessionDetail): the salesperson's lines as bubbles on the
 * right, the AI customer's on the left, each sized to its text, and every alert
 * card on the side of whoever triggered it (owner, 2026-09-26).
 */
export type TrainingTimelineEventType = 'transcript' | 'detection' | 'landmine' | 'competitor' | 'evaluation';

export interface TrainingTimelineEventData {
  type: TrainingTimelineEventType;
  timestamp: string;
  data: any;
}

const COPY = {
  salesperson: 'Salesperson',
  aiCustomer: 'AI customer',
  landmine: 'Landmine triggered',
  competitor: 'Competitor mentioned',
} as const;

/** Anything a customer or the AI customer said or triggered sits on the left. */
const isSalespersonSide = (speaker?: string): boolean => speaker === 'salesperson';

export function timelineEventSide(event: TrainingTimelineEventData): 'left' | 'right' {
  if (event.type === 'detection') return 'left'; // question cards come from the customer's lines
  return isSalespersonSide(event.data?.speaker) ? 'right' : 'left';
}

const TranscriptBubble: React.FC<{ speaker?: string; text?: string; timeLabel: string }> = ({ speaker, text, timeLabel }) => {
  const isSalesperson = isSalespersonSide(speaker);
  const color = getSpeakerColor({ speaker: isSalesperson ? 'salesperson' : 'customer' });
  return (
    <div className="max-w-[78%] min-w-[120px]">
      <div className={`flex items-center gap-2 mb-1 ${isSalesperson ? 'flex-row-reverse' : ''}`}>
        <span className="text-xs font-semibold" style={{ color: isSalesperson ? color.border : color.text }}>
          {isSalesperson ? COPY.salesperson : COPY.aiCustomer}
        </span>
        <span className="text-[10px] text-ink-muted">{timeLabel}</span>
      </div>
      <div
        className="px-3.5 py-2 rounded-2xl shadow-sm"
        style={{
          backgroundColor: color.bg,
          color: color.text,
          border: `1px solid ${color.border}`,
          borderBottomRightRadius: isSalesperson ? 6 : undefined,
          borderBottomLeftRadius: isSalesperson ? undefined : 6,
        }}
      >
        <div className="text-sm whitespace-pre-wrap">{text}</div>
      </div>
    </div>
  );
};

const LANDMINE_TONE: Record<string, string> = {
  high: 'bg-red-50 border-red-500',
  medium: 'bg-orange-50 border-orange-400',
};

const AlertCard: React.FC<{ event: TrainingTimelineEventData; timeLabel: string }> = ({ event, timeLabel }) => {
  const isLandmine = event.type === 'landmine';
  const tone = isLandmine
    ? LANDMINE_TONE[event.data?.priority] ?? 'bg-yellow-50 border-yellow-400'
    : 'bg-purple-50 border-purple-400';
  return (
    <div className={`p-3 rounded-lg border-l-4 ${tone}`}>
      <div className="flex justify-between items-start gap-3 mb-1">
        <span className={`text-xs font-semibold ${isLandmine ? 'text-red-700' : 'text-purple-700'}`}>
          {isLandmine ? COPY.landmine : COPY.competitor}
        </span>
        <span className="text-xs text-ink-muted">{timeLabel}</span>
      </div>
      <div className="text-sm font-medium">{isLandmine ? event.data?.trigger : event.data?.competitor}</div>
      {event.data?.matched_text && (
        <div className="text-xs text-ink-secondary mt-1">{event.data.matched_text}</div>
      )}
    </div>
  );
};

interface TrainingTimelineEventProps {
  event: TrainingTimelineEventData;
  timeLabel: string;
  inquiryActions?: InquiryCardActions;
}

export const TrainingTimelineEvent: React.FC<TrainingTimelineEventProps> = ({ event, timeLabel, inquiryActions }) => {
  const side = timelineEventSide(event);
  const align = side === 'right' ? 'justify-end' : 'justify-start';
  let content: React.ReactNode = null;
  if (event.type === 'transcript') {
    content = <TranscriptBubble speaker={event.data?.speaker} text={event.data?.text} timeLabel={timeLabel} />;
  } else if (event.type === 'detection') {
    content = (
      <div className="max-w-[78%] w-full">
        <TrainingDetectionCard
          detection={event.data as TrainingQuestionDetection}
          timeLabel={timeLabel}
          inquiryActions={inquiryActions}
        />
      </div>
    );
  } else if (event.type === 'landmine' || event.type === 'competitor') {
    content = (
      <div className="max-w-[78%] w-full">
        <AlertCard event={event} timeLabel={timeLabel} />
      </div>
    );
  }
  if (!content) return null;
  return (
    <div data-ts={event.timestamp} data-testid={`timeline-${event.type}-${side}`} className={`flex ${align}`}>
      {content}
    </div>
  );
};

export default TrainingTimelineEvent;
